import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames/bind';
import { Icon } from '../../shared/Icon/index.tsx';

import { isIOS, isAndroidChrome } from '../../../helpers/serverRenderingUtils';
import Text from '../../shared/Text/index.tsx';

import styles from './styles.sass';

const cx = classnames.bind(styles);

const isMobile = isIOS() || isAndroidChrome();

const FieldInputPresentationComponent = ({
  isInline,
  name,
  label,
  labelWidth,
  type,
  onChange,
  placeholder,
  value,
  minNumberValue,
  autofocus,
  maxCharacterLength,
  isShowingError,
  error,
  iconType,
  onClear,
}) => {
  const isShowingBottomSection = maxCharacterLength || isShowingError;
  const showClearButton = !!onClear && value !== '';
  return (
    <div className={styles.wrapper}>
      <label
        className={cx({ label: true, inlineLabel: isInline })}
        htmlFor={name}
      >
        {label && (
          <div
            className={cx({
              labelTextWrapper: true,
              labelTextWrapperInline: isInline,
            })}
            style={{ width: labelWidth }}
          >
            <Text color="#292f36" size="lg" marginBottom={10}>
              {label}
            </Text>
          </div>
        )}
        <div className={styles.inputContainer}>
          {iconType && (
            <div className={styles.iconWrapper}>
              <Icon type={iconType} fillColor="#dedfe0" />
            </div>
          )}
          <input
            className={cx({
              input: true,
              inlineInput: isInline,
              numberInput: type === 'number',
              numberInputMobile: isMobile,
              inputError: isShowingError,
              inputWithClearButton: onClear,
              inputWithIcon: iconType,
            })}
            name={name}
            onChange={onChange}
            placeholder={placeholder}
            value={value}
            type={type}
            min={type === 'number' ? minNumberValue : null}
            autoFocus={autofocus}
          />
          {showClearButton && (
            <button
              type="button"
              onClick={onClear}
              name="Clear input"
              className={styles.clearButton}
            >
              <Icon type="x" fillColor="#fff" />
            </button>
          )}
        </div>
      </label>
      {isShowingBottomSection && (
        <div className={styles.bottomSection}>
          <div
            className={cx({
              errorText: true,
              errorTextVisible: isShowingError,
            })}
          >
            <Text color="#d0021b">{error}</Text>
          </div>
          {maxCharacterLength && (
            <Text size="sm" color="#7f8287">
              {`${value.length} / ${maxCharacterLength}`}
            </Text>
          )}
        </div>
      )}
    </div>
  );
};

const FieldInput = ({
  isInline,
  name,
  label,
  labelWidth,
  type,
  onChange,
  placeholder,
  defaultValue,
  minNumberValue,
  autofocus,
  maxCharacterLength,
  error,
  iconType,
  onClear,
  isShowingError,
}) => {
  const [value, setValue] = useState(defaultValue);
  return (
    <FieldInputPresentationComponent
      isInline={isInline}
      name={name}
      label={label}
      labelWidth={labelWidth}
      type={type}
      onChange={e => {
        setValue(e.currentTarget.value);
        onChange(e);
      }}
      placeholder={placeholder}
      value={value}
      minNumberValue={minNumberValue}
      autofocus={autofocus}
      maxCharacterLength={maxCharacterLength}
      isShowingError={isShowingError}
      error={error}
      iconType={iconType}
      onClear={
        onClear
          ? () => {
              setValue('');
              onClear();
            }
          : null
      }
    />
  );
};

const FieldInputWithRedux = ({
  input: { name, onChange, value },
  meta: { error, valid, submitFailed },
  type,
  label,
  placeholder,
  maxCharacterLength,
  isInline,
  labelWidth,
  minNumberValue,
  autofocus,
}) => (
  <FieldInputPresentationComponent
    isInline={isInline}
    name={name}
    label={label}
    labelWidth={labelWidth}
    type={type}
    onChange={onChange}
    placeholder={placeholder}
    value={value}
    minNumberValue={minNumberValue}
    autofocus={autofocus}
    maxCharacterLength={maxCharacterLength}
    isShowingError={submitFailed && !valid}
    error={error}
  />
);

const inputTypes = ['number', 'text', 'email', 'password'];

FieldInput.defaultProps = {
  isInline: false,
  autofocus: false,
  labelWidth: null,
  maxCharacterLength: null,
  minNumberValue: 0,
  placeholder: null,
  name: null,
  label: null,
  isShowingError: false,
  error: null,
  defaultValue: '',
};

FieldInput.propTypes = {
  isInline: PropTypes.bool,
  name: PropTypes.string,
  label: PropTypes.string,
  labelWidth: PropTypes.number,
  type: PropTypes.oneOf(inputTypes).isRequired,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  defaultValue: PropTypes.string,
  minNumberValue: PropTypes.number,
  autofocus: PropTypes.bool,
  maxCharacterLength: PropTypes.number,
  isShowingError: PropTypes.bool,
  error: PropTypes.string,
};

FieldInputPresentationComponent.defaultProps = {
  isInline: false,
  autofocus: false,
  labelWidth: null,
  maxCharacterLength: null,
  minNumberValue: 0,
  placeholder: null,
  name: null,
  label: null,
  isShowingError: false,
  error: null,
  value: null,
};

FieldInputPresentationComponent.propTypes = {
  isInline: PropTypes.bool,
  name: PropTypes.string,
  label: PropTypes.string,
  labelWidth: PropTypes.number,
  type: PropTypes.oneOf(inputTypes).isRequired,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  value: PropTypes.string,
  minNumberValue: PropTypes.number,
  autofocus: PropTypes.bool,
  maxCharacterLength: PropTypes.number,
  isShowingError: PropTypes.bool,
  error: PropTypes.string,
};

FieldInputWithRedux.defaultProps = {
  isInline: false,
  autofocus: false,
  labelWidth: null,
  maxCharacterLength: null,
  minNumberValue: 0,
  placeholder: null,
  label: null,
};

FieldInputWithRedux.propTypes = {
  input: PropTypes.shape({
    name: PropTypes.string,
    onBlur: PropTypes.func,
    onChange: PropTypes.func,
    onDragStart: PropTypes.func,
    onDrop: PropTypes.func,
    onFocus: PropTypes.func,
    value: PropTypes.string,
  }).isRequired,
  meta: PropTypes.shape({
    active: PropTypes.bool,
    asyncValidating: PropTypes.bool,
    autofilled: PropTypes.bool,
    dirty: PropTypes.bool,
    dispatch: PropTypes.func,
    form: PropTypes.string,
    initial: PropTypes.string,
    invalid: PropTypes.bool,
    pristine: PropTypes.bool,
    submitFailed: PropTypes.bool,
    submitting: PropTypes.bool,
    touched: PropTypes.bool,
    valid: PropTypes.bool,
    visited: PropTypes.bool,
  }).isRequired,
  placeholder: PropTypes.string,
  label: PropTypes.string,
  type: PropTypes.oneOf(inputTypes).isRequired,
  maxCharacterLength: PropTypes.number,
  labelWidth: PropTypes.number,
  isInline: PropTypes.bool,
  minNumberValue: PropTypes.number,
  autofocus: PropTypes.bool,
};

export { FieldInput, FieldInputWithRedux };
